function formatMoney(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.initializeQuantityButtons();
  }

  initializeUpsellButtons() {
    var upsellCheckboxes = this.querySelectorAll('.upsell-checkbox');
    upsellCheckboxes.forEach(checkbox => {
        checkbox.removeEventListener('change', this.handleUpsellChange);
        checkbox.addEventListener('change', this.handleUpsellChange.bind(this));
    });
}

initializeQuantityButtons() {
  const drawerInner = this.querySelector('.drawer__inner');
  drawerInner.removeEventListener('click', this.handleQuantityButtonClick);
  drawerInner.addEventListener('click', this.handleQuantityButtonClick.bind(this));
}

handleQuantityButtonClick(event) {
  if (event.target.closest('.quantity__button')) {
    setTimeout(() => {
      this.updateSubtotal();
    }, 500); // Adjust the delay as needed to allow the cart to update
  }
}

updateSubtotal() {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      const subtotalElement = this.querySelector('.totals__total-value');
      if (subtotalElement) {
        subtotalElement.textContent = formatMoney(cart.total_price);
      }
    })
    .catch(error => {
      console.error('Error updating subtotal:', error);
    });
}

handleUpsellChange(event) {
    var checkbox = event.target;
    var productId = checkbox.getAttribute('data-product-id');
    if (checkbox.checked) {
        this.addProductToCart(productId);
    } else {
        this.removeProductFromCart(productId);
    }
}

addProductToCart(productId) {
    fetch('/cart/add.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            items: [{
                id: productId,
                quantity: 1
            }]
        })
    })
    .then(response => response.json())
    .then(data => {
        this.dispatchEvent(new CustomEvent('cartUpdated', { bubbles: true }));
        this.updateSubtotal(); // Add this line
    })
    .catch(error => {
        console.error('Error adding product to cart:', error);
    });
}

removeProductFromCart(productId) {
  fetch('/cart.js')
      .then(response => {
          if (!response.ok) {
              throw new Error('Failed to fetch cart: ' + response.statusText);
          }
          return response.json();
      })
      .then(cart => {
          const item = cart.items.find(item => item.variant_id === parseInt(productId));
          if (!item) {
              throw new Error('Item not found in cart');
          }
          return fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  id: item.key, // Using the unique key of the line item
                  quantity: 0
              })
          });
      })
      .then(response => {
          if (!response.ok) {
              throw new Error('Failed to remove product from cart: ' + response.statusText);
          }
          return response.json();
      })
      .then(data => {
          this.dispatchEvent(new CustomEvent('cartUpdated', { bubbles: true }));
          this.updateSubtotal(); // Add this line
      })
      .catch(error => {
          console.error('Error removing product from cart:', error);
      });
}

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
        this.initializeUpsellButtons(); // Reinitialize buttons after drawer opens
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
      this.initializeQuantityButtons();
    });

    this.dispatchEvent(new CustomEvent("cartUpdated", { bubbles: true }));
    this.initializeUpsellButtons(); // Reinitialize buttons after content update
    this.updateSubtotal(); // Add this line

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);